/**
 * The repositories approved for the organization's GitHub app (security review
 * SEC-04), under the app panel on the GitHub page of its integrations. A token
 * is minted for a workspace only over its repositories on this list: what a
 * workspace names is not enough, since any member can make one.
 *
 * Every member sees the list; an owner or an admin adds and removes, with the
 * repositories the app's installation reaches offered as they type.
 */
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { githubRepoName, type OrgGithubReachable, type OrgGithubRepos } from '@airlock/contracts/cloud/github-app';
import { color, font } from '../../theme/tokens';
import { Note } from '../../components/list';
import { Input, Kicker, RowAction } from '../../components/primitives';
import { HeaderButton } from '../../components/Shell';
import { api, apiPath } from '../../lib/api';
import { proseDay } from '../../lib/format';
import { failureText } from './shared';

export function GithubReposPanel({ orgId, admin }: { orgId: string; admin: boolean }): React.JSX.Element | null {
  const [list, setList] = useState<OrgGithubRepos | null>(null);
  const [reach, setReach] = useState<string[]>([]);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setList(await api.get<OrgGithubRepos>(apiPath`/orgs/${orgId}/github/repos`));
    } catch (failure) {
      setError(failureText(failure));
    }
  }, [orgId]);

  useEffect(() => {
    void load();
    if (!admin) return;
    let live = true;
    // Suggestions only: GitHub not answering leaves the field to be typed in.
    void api
      .get<OrgGithubReachable>(apiPath`/orgs/${orgId}/github/repos/reachable`)
      .then((answer) => live && setReach(answer.repositories ?? []))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [load, admin, orgId]);

  const approve = async (): Promise<void> => {
    const repo = githubRepoName(typed);
    if (!repo) {
      setError('Name a GitHub repository as owner/name.');
      return;
    }
    try {
      setList(await api.post<OrgGithubRepos>(apiPath`/orgs/${orgId}/github/repos`, { repo }));
      setTyped('');
      setError(null);
    } catch (failure) {
      setError(failureText(failure));
    }
  };

  const remove = async (repo: string): Promise<void> => {
    const [owner, name] = repo.split('/');
    try {
      setList(await api.del<OrgGithubRepos>(apiPath`/orgs/${orgId}/github/repos/${owner}/${name}`));
    } catch (failure) {
      setError(failureText(failure));
    }
  };

  if (!list) return error ? <Note tone="warn">{error}</Note> : null;
  const approved = new Set(list.repositories.map((one) => one.repo));
  const offered = reach.filter((repo) => !approved.has(repo));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '18px 0 16px', borderBottom: `1px solid ${color.line}` }}>
      <Kicker>Approved repositories</Kicker>
      <span style={{ fontSize: 13.5, color: color.ink, lineHeight: 1.5 }}>
        The app hands members’ machines a token only for a workspace’s repositories on this list.
        {list.canEdit ? ' Add each repository your workspaces push to.' : ' An owner or an admin of your organization changes it.'}
      </span>
      {list.repositories.length === 0 ? (
        <Note>No repository is approved yet, so members’ machines use their own GitHub sign-in everywhere.</Note>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {list.repositories.map((one) => (
            <div
              key={one.repo}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                padding: '8px 0',
                boxShadow: `inset 0 -1px 0 ${color.line}`
              }}
            >
              <span style={{ minWidth: 0, fontFamily: font.mono, fontSize: 13, color: color.ink, overflowWrap: 'anywhere' }}>{one.repo}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 13, color: color.inkSubtle }}>
                <span>
                  {one.addedBy.label}, {proseDay(one.addedAt)}
                </span>
                {list.canEdit && (
                  <RowAction danger label={`Remove ${one.repo}`} onClick={() => void remove(one.repo)}>
                    Remove
                  </RowAction>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      {list.canEdit && (
        <form
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
          onSubmit={(event) => {
            event.preventDefault();
            void approve();
          }}
        >
          <Input
            aria-label="Repository to approve"
            placeholder="owner/name"
            list={`github-reach-${orgId}`}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            style={{ flex: '1 1 260px', width: 'auto', fontFamily: font.mono, fontSize: 13 }}
          />
          <datalist id={`github-reach-${orgId}`}>
            {offered.map((repo) => (
              <option key={repo} value={repo} />
            ))}
          </datalist>
          <HeaderButton onClick={() => void approve()}>Approve</HeaderButton>
        </form>
      )}
      {error && <Note tone="warn">{error}</Note>}
    </div>
  );
}
